import React from 'react'
import Link from 'next/link'
import { Breadcrumb } from '../commons/Breadcrumb'
import { DarkFaq } from '../index/service-category/personal-trainer/DarkFaq'
import { PersonalTrainerWhyUs } from '../index/service-category/personal-trainer/PersonalTrainerWhyUs'
import TrainerValueProposition from '../index/service-category/personal-trainer/TrainerValueProposition'
import { MarketHead } from './MarketHead'
import { MarketBooking } from './MarketBooking'
import { lowestSessionPrice } from './PtPanels'
import { LinkCards, MarketSection } from './ui'
import { MarketPtHero } from './MarketPtHero'
import { MarketPtPackages, MarketPtReviews } from './PtSections'
import { ContentSection } from './AirconServicePage'
import { buildContext, guideLinks, ptHubPath, ptTopicLinks, resolveAll } from './content'
import { getPtSections } from './sections'
import { FAQ_COUNTS, mergeFaqs, ptFaqBank } from './faqs'
import { breadcrumbNode, faqNode, graph, serviceNode, webPageNode } from '../../utils/market-schema'
import { getPersonalTrainerPackages } from '../../data/PersonalTrainerPackages'
import { PT_TOPICS, marketKey } from '../../data/markets/registry'
import { getPtModifiers } from '../../utils/pt-package-price'

// hreflang set for the personal trainer hubs (Singapore / Dubai / KL).
// Abu Dhabi is en-AE like Dubai, so it stands alone - see AIRCON_HUB_ALTERNATES.
export const PT_HUB_ALTERNATES = [
    { hreflang: 'en-sg', path: '/sg/personal-trainer' },
    { hreflang: 'en-ae', path: '/ae/personal-trainer-dubai' },
    { hreflang: 'en-my', path: '/my/personal-trainer-kuala-lumpur' },
    // The Malay KL page is the same offer in Bahasa Melayu.
    { hreflang: 'ms-my', path: '/my/personal-trainer-kuala-lumpur/jurulatih-peribadi-kl' },
]

const ptOffers = (market, city) => {
    const pkgs = getPersonalTrainerPackages(market.countryCd)
    return ['Signature', 'Elite'].flatMap((tier) =>
        pkgs[tier].packages.map((p) => ({
            name: `${tier} personal training, ${p.totalServices} sessions${city ? `, ${city.name}` : ''}`,
            price: p.perSessionPrice * p.totalServices,
        }))
    )
}

/**
 * UAE / Malaysia personal trainer page: the city hub (`isHub`) or a topic page
 * (pricing, weight loss, female, freelance, seniors, pre/postnatal).
 *
 * Laid out like the Singapore personal trainer pages (dark theme): hero with
 * the free consultation, the page's own sections, why us, reviews, goals,
 * package selector, FAQ - built from the same components.
 *
 * Trainers: the hero shows "how it works" instead of a trainer strip, and
 * nothing lists or links to trainers, until the market's trainerRoster switch
 * is on (utils/markets.js).
 */
export const PtTopicPage = ({ countryCd, citySlug, requestCategorySettings, modifiedDate, testimonials = [], content: raw, isHub = false }) => {
    const ctx = buildContext({ countryCd, citySlug, tables: null })
    const { market, city } = ctx
    // Content modules quote package prices through these helpers, e.g.
    // c.pt('Signature', 30) -> per-session price, c.mods.female -> premium.
    const pkgs = getPersonalTrainerPackages(countryCd)
    const pt = (tier, sessions) => pkgs[tier].packages.find((p) => p.totalServices === sessions).perSessionPrice
    const ptCtx = { ...ctx, lowest: lowestSessionPrice(countryCd), pt, mods: getPtModifiers(countryCd) }
    const content = resolveAll(raw, ptCtx)
    // Page questions first, then the market FAQ bank (Singapore-level depth).
    const faqBank = ptFaqBank(ptCtx)
    const faqs = isHub
        ? mergeFaqs(FAQ_COUNTS.hub, content.faqs, faqBank.hub)
        : mergeFaqs(FAQ_COUNTS.page, content.faqs, faqBank.topic, faqBank.hub)
    const sections = getPtSections(ctx)
    const hubPath = ptHubPath(market, citySlug)
    const path = isHub ? hubPath : `${hubPath}/${content.slug}`
    const ratesPage = (PT_TOPICS[marketKey(countryCd, citySlug)] ?? []).find((p) => /price|rates/.test(p.slug))
    const guides = guideLinks(market, citySlug, 'pt', [content.slug])

    const jsonLd = graph(
        webPageNode({ market, path, title: content.title, description: content.description, dateModified: modifiedDate, inLanguage: content.inLanguage }),
        breadcrumbNode(path, content.h1),
        serviceNode({
            market,
            city,
            path,
            name: content.schema?.name ?? content.h1,
            serviceType: content.schema?.serviceType ?? 'Personal training',
            description: content.description,
            offers: ptOffers(market, city),
            catalogName: `Personal training packages in ${city.name}`,
        }),
        faqs.length ? faqNode(path, faqs) : null,
    )

    return (
        <>
            <MarketHead
                market={market}
                path={path}
                title={content.title}
                description={content.description}
                jsonLd={jsonLd}
                alternates={content.alternates ?? []}
                ogImageAlt={content.imageAlt}
                contentLanguage={content.contentLanguage}
            />
            <MarketBooking market={market} city={city} requestType="Fitness Trainer" requestCategorySettings={requestCategorySettings} inlineForm>
                <MarketPtHero
                    market={market}
                    city={city}
                    title={content.h1}
                    intro={content.lead}
                    heroCopy={content.heroCopy}
                    image={content.image}
                />
                <div className="bg-slate-900">
                    <Breadcrumb pageTitle={content.h1} jsonLd={false} />
                </div>

                {(content.sections ?? []).map((section, i) => (
                    <ContentSection key={i} id={section.id} section={section} dark />
                ))}

                <PersonalTrainerWhyUs title={sections.whyUs.title} features={sections.whyUs.features} />
                <MarketPtReviews testimonials={testimonials} city={city} />
                <TrainerValueProposition
                    title={sections.valueProp.title}
                    intro={sections.valueProp.intro}
                    locations={sections.valueProp.locations}
                    goals={sections.goals.filter((goal) => !goal.link.endsWith(`/${content.slug}`))}
                />
                <MarketPtPackages
                    title={content.packagesTitle || `Personal Trainer Rates & Packages in ${city.name}`}
                    priceListHref={ratesPage && ratesPage.slug !== content.slug ? `${hubPath}/${ratesPage.slug}` : hubPath}
                    priceListLabel={ratesPage && ratesPage.slug !== content.slug ? `Full ${ratesPage.title.toLowerCase()}` : `Personal training in ${city.name}`}
                    note={content.packagesHighlight}
                />

                {faqs.length > 0 && (
                    <section className="relative py-8 md:py-12 overflow-hidden bg-slate-900">
                        <div className="max-w-4xl mx-auto px-4">
                            <DarkFaq title={content.faqTitle || 'Questions people ask'} questionsAnswers={faqs} showSearch={false} jsonLd={false} />
                        </div>
                    </section>
                )}

                <MarketSection tone="dark" title={isHub ? `Personal training in ${city.name}: more topics` : `More on personal training in ${city.name}`}>
                    <LinkCards dark links={ptTopicLinks(market, citySlug, [content.slug])} />
                    {guides.length > 0 && (
                        <div className="mt-8">
                            <h3 className="mb-3 font-bold text-white">Guides</h3>
                            <LinkCards dark links={guides} />
                        </div>
                    )}
                    {!isHub && (
                        <p className="mt-8 text-sm text-white/60">
                            Back to <Link href={hubPath} className="font-semibold text-orange-400 hover:underline">Personal Trainer {city.name}</Link>
                        </p>
                    )}
                </MarketSection>

                {/* The trainer recruitment panel appears on the hub and the
                    freelance page only: repeated on every topic page it made the
                    Dubai and Abu Dhabi pages read as the same template. */}
                {(isHub || content.trainerCta) && (
                <MarketSection tone="dark" narrow>
                    <div className="rounded-3xl border border-white/10 bg-white/5 p-8 text-center text-white">
                        <p className="text-xl font-bold">Are you a certified personal trainer in {city.name}?</p>
                        <p className="mt-2 text-slate-300">We are building our founding team of trainers in {city.name}. Clients come to you; you focus on coaching.</p>
                        <div className="mt-5">
                            <Link href="/service-provider-signup" className="inline-flex items-center gap-2 rounded-full bg-orange-500 px-6 py-3 font-bold text-white transition hover:bg-orange-600">
                                Apply as a founding trainer
                            </Link>
                        </div>
                    </div>
                </MarketSection>
                )}
            </MarketBooking>
        </>
    )
}

export const PtCityHub = (props) => <PtTopicPage {...props} isHub />
